<template>
  <v-row>
    <v-col>
      <v-radio-group v-model="program">
        <v-radio :label="$t('doNotProgram')" :value="false" />
        <v-radio :value="true">
          <template v-slot:label>
            {{ $t('program') }}
            <v-menu
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :disabled="!program"
                  :loading="loadingSchedule"
                  v-model="liveAtLocale"
                  class="ml-4 mb-n6"
                  append-icon="mdi-menu-swap"
                  readonly
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="liveAtDate"
                @input="onChangeLiveAtDate"
              ></v-date-picker>
              <v-time-picker
                v-model="liveAtTime"
                @input="onChangeLiveAtTime"
                format="24hr"
                :allowedMinutes="(m) => m % 15 === 0"
              ></v-time-picker>
            </v-menu>
          </template>
        </v-radio>
      </v-radio-group>
      <div v-if="program && liveAtDate" class="ml-10 mt-n8">
        <v-row class="mt-4">
          <v-switch
            v-model="repeatProgramDate"
            class="mt-5"
            inset
            :label="$t('repeat')"
          ></v-switch>
        </v-row>
        <v-row v-if="repeatProgramDate" class="mt-0">
          <v-col cols="12">
            <v-row class="mt-n6" align="center">
              <v-col cols="4">{{ $t('frequency') }}</v-col>
              <v-col cols="8">
                <v-select
                  v-model="freqChoice"
                  :items="[
                    { text: $t('everyDay'), value: 'DAY' },
                    { text: $t('everyWeek'), value: 'WEEK' },
                    { text: $t('everyMonth'), value: 'MONTH' },
                    { text: $t('everyYear'), value: 'YEAR' },
                  ]"
                  :loading="loadingSchedule"
                />
              </v-col>
            </v-row>

            <v-row class="mt-n6" align="center" v-if="freqChoice === 'YEAR'">
              <v-col cols="4">{{ $t('month') }}</v-col>
              <v-col cols="8"
                ><v-select
                  v-model="schedule.month_choice"
                  :items="monthsInYear"
                  :loading="loadingSchedule"
              /></v-col>
            </v-row>

            <v-row
              class="mt-n6"
              align="center"
              v-if="['MONTH', 'YEAR'].includes(freqChoice)"
            >
              <v-col cols="4">{{ $t('date') }}</v-col>
              <v-col cols="8"
                ><v-select
                  v-model="schedule.date_choice"
                  :items="daysInMonth"
                  :loading="loadingSchedule"
              /></v-col>
            </v-row>

            <v-row class="mt-n6" align="center" v-if="freqChoice === 'WEEK'">
              <v-col cols="4">{{ $t('day') }}</v-col>
              <v-col cols="8">
                <v-select
                  v-model="schedule.day_choice"
                  :items="daysInWeek"
                  :loading="loadingSchedule"
                />
              </v-col>
            </v-row>

            <v-row class="mt-n6" align="center" v-if="freqChoice !== undefined">
              <v-col cols="4">{{ $t('hour') }}</v-col>
              <v-col cols="8">
                <v-menu
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="timeChoice"
                      append-icon="mdi-menu-down"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      :loading="loadingSchedule"
                    ></v-text-field>
                  </template>
                  <v-time-picker
                    v-model="timeChoice"
                    format="24hr"
                    :allowedMinutes="(m) => m % 15 === 0"
                  ></v-time-picker>
                </v-menu>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12">
            <small class="text-caption grey--text">
              {{ $t('carefulUTC') }}
            </small>
          </v-col>
        </v-row>
      </div>
    </v-col>
  </v-row>
</template>

<script>
export default {
  name: 'ProgrammationDate',
  props: {
    loadingSchedule: {
      type: Boolean,
      default: true,
    },
    schedule: {
      type: Object,
      default: () => ({
        freq_choice: 'NONE',
        minute_choice: '00',
        hour_choice: '00',
        day_choice: 0,
        date_choice: 1,
        month_choice: 1,
        live_at: new Date().toISOString().slice(0, 19),
      }),
    },
  },
  data() {
    return {
      program: false,
      liveAtDate: new Date().toISOString().slice(0, 10),
      liveAtTime: '23:45',
      repeatProgramDate: false,
      timeChoice: '23:45',
      freqChoice: 'DAY',
      loaded: false,
    }
  },
  watch: {
    schedule: {
      deep: true,
      handler(value) {
        // hack to detect first load
        if (value.live_at?.length === 19) {
          this.repeatProgramDate = value.freq_choice !== 'NONE'
          this.freqChoice = value.freq_choice
          this.schedule.live_at += 'Z' // make it explicitely UTC
          this.program = !!value.live_at
          this.timeChoice = '00:00'
          return
        }
        const localLiveAt = this.getLocaleDateString(value.live_at)

        this.liveAtDate = localLiveAt.substring(0, 10)
        this.liveAtTime = `${localLiveAt.substring(
          11,
          13
        )}:${localLiveAt.substring(14, 16)}`

        if (this.loaded) {
          this.$emit('changeSchedule', this.schedule)
        } else {
          this.loaded = true
        }
        this.timeChoice =
          (value.hour_choice ?? '00') + ':' + (value.minute_choice ?? '00')
      },
    },
    timeChoice(value) {
      const [hours, minutes] = value.split(':')
      this.schedule.hour_choice = hours ?? '00'
      this.schedule.minute_choice = minutes ?? '00'
    },
    program(value) {
      this.schedule.freq_choice = value ? this.freqChoice : 'NONE'
      if (!value) {
        this.schedule.live_at = ''
      }
    },
    repeatProgramDate(value) {
      this.schedule.freq_choice = value ? this.freqChoice : 'NONE'
    },
    freqChoice(value) {
      if (this.repeatProgramDate && this.program) {
        this.schedule.freq_choice = value
      }
    },
  },
  methods: {
    // schedule.live_at is always UTC thanks to .toISOString()
    // however, we display local time to the user,
    // so we need to do a little bit of work here and there
    getLocaleDate(dateStr) {
      if (!dateStr.trim()) {
        return new Date()
      }
      if (dateStr.indexOf('Z') === -1) {
        dateStr += 'Z'
      }
      return new Date(dateStr)
    },
    // hacky way to get the right substrings in schedule watcher
    getLocaleDateString(dateStr) {
      const date = this.getLocaleDate(dateStr ?? '')
      return new Date(date - date.getTimezoneOffset() * 60000).toISOString()
    },
    onChangeLiveAtTime(time) {
      const live_at = this.getLocaleDate(this.schedule.live_at)
      const [hours, minutes] = time.split(':')

      live_at.setHours(hours, minutes)
      this.schedule.live_at = live_at.toISOString()
    },
    onChangeLiveAtDate(value) {
      const localLiveAt = this.getLocaleDate(this.schedule.live_at)
      const date = new Date(value)

      date.setHours(localLiveAt.getHours(), localLiveAt.getMinutes())
      this.schedule.live_at = date.toISOString()
    },
  },
  computed: {
    liveAtLocale() {
      return `${this.liveAtDate} ${this.$t('dateATtime')} ${this.liveAtTime}`
    },
    daysInWeek() {
      return [
        this.$t('monday'),
        this.$t('tuesday'),
        this.$t('wednesday'),
        this.$t('thursday'),
        this.$t('friday'),
        this.$t('saturday'),
        this.$t('sunday'),
      ].map((d, i) => ({ text: d, value: i }))
    },
    daysInMonth() {
      const days = []
      for (let i = 1; i <= 28; i++) {
        days.push({
          text: `${this.$t('the')} ${i}`,
          value: i.toString().padStart(2, '0'),
        })
      }
      days.push({
        text: this.$t('twoDaysBeforeEndMonth'),
        value: '-2',
      })
      days.push({ text: this.$t('dayBeforeEndMonth'), value: '-1' })
      days.push({ text: this.$t('lastDayOfMonth'), value: '00' })
      return days
    },
    monthsInYear() {
      return [
        this.$t('january'),
        this.$t('february'),
        this.$t('march'),
        this.$t('april'),
        this.$t('may'),
        this.$t('june'),
        this.$t('july'),
        this.$t('august'),
        this.$t('september'),
        this.$t('october'),
        this.$t('november'),
        this.$t('december'),
      ].map((m, i) => ({ text: m, value: i + 1 }))
    },
  },
}
</script>
